import type { Booking } from "@common/types";

// A trip whose start date/time has already passed
export function hasTripStartPassed(booking: Booking): boolean {
  const startTime = booking.customStartTime || booking.timeSlot;
  const tripStart = new Date(`${booking.date}T${startTime}`);
  return tripStart.getTime() < Date.now();
}

// Status badge text, with the Fortnox invoice sub-status appended when relevant
export function getBookingStatusLabel(booking: Booking, label: (status: string) => string = status => status): string {
  if (
    (booking.status === "invoiced" || booking.status === "completed") &&
    booking.fortnoxInvoiceStatus &&
    booking.fortnoxInvoiceStatus !== "cancelled"
  ) {
    return `${label(booking.status)} (${label(booking.fortnoxInvoiceStatus)})`;
  }
  return label(booking.status);
}

/**
 * When a booking starts and ends (HH:mm). The start is the custom start time or
 * the time slot; the end adds the custom duration, else the trip's duration,
 * else 3 hours. `end` is null when the start is not a readable time.
 */
export function getBookingTimes(booking: Booking): { start: string; end: string | null } {
  const start = booking.customStartTime || booking.timeSlot;
  const [hours, minutes] = (start ?? "").split(":").map(Number);
  if (!start || isNaN(hours) || isNaN(minutes)) return { start: start ?? "", end: null };

  const durationHours =
    booking.customDurationHours || (booking.tripDurationMinutes ? booking.tripDurationMinutes / 60 : 3);
  const totalMinutes = hours * 60 + minutes + durationHours * 60;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = Math.floor(totalMinutes % 60);
  return {
    start,
    end: `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`,
  };
}
